import { concreteStreamingMode } from './api/v1/helpers'
import { InputPort, IpPortMode, PortMode, RistProfile, RistSimpleProfileInputPort } from './api/v1/types'
import { BadRequestError, ConflictError, ErrorCode } from './errors'
import { group } from './utils'
import { isBondedSrtInputPort, isRtpOverSrt } from './srt'

function setPriorityBits(num: number, priority2bits: ChannelPriority) {
    // set bit 29 and 30 to encode the priority (we don't want to use bit 31 since it is the sign bit)
    return BigInt(num) | (BigInt(priority2bits) << 29n)
}

function getPriorityBits(num: number) {
    // get bit 29 and 30
    return (BigInt(num) >> 29n) & 3n
}

export function clear3HighOrderBitsOnInt32(num: number) {
    return Number(BigInt(num) & 0x1fffffffn)
}

export type ChannelPriority = 0 | 1 | 2 | 3
export const MIN_CHANNEL_PRIO = 0
export const MAX_CHANNEL_PRIO = 3
export const MAX_CHANNELS_PER_APPLIANCE = 2

// Channel priority is encoded in the 2 high order bits of the channel
// in order to retain compatibility with the pre-existing channels
// which all have priority 0.
export function channelIdFromChannelGroupAndPriority(channelGroup: number, priority: ChannelPriority) {
    return Number(BigInt.asIntN(32, setPriorityBits(channelGroup, priority)))
}

export function priorityFromChannel(channelId: number) {
    return Number(getPriorityBits(channelId)) as ChannelPriority
}

export function channelGroupFromChannel(channelId: number) {
    return clear3HighOrderBitsOnInt32(channelId)
}

export function validateChannelPriority(
    ports: Array<{
        port: InputPort
        appliance: {
            id: string
        }
    }>,
    hasDerivedInputs: boolean
) {
    const allPortsHaveAChannelPriority = ports.every((p) => p.port.priority !== undefined)
    if (!allPortsHaveAChannelPriority) {
        throw new BadRequestError(ErrorCode.badRequest, 'All ports must have a channel priority.')
    }

    const allPrioritiesAreInValidRange = ports.every(
        (p) => p.port.priority! >= MIN_CHANNEL_PRIO && p.port.priority! <= MAX_CHANNEL_PRIO
    )
    if (!allPrioritiesAreInValidRange) {
        throw new BadRequestError(
            ErrorCode.badRequest,
            `All ports must have a channel priority in the range [${MIN_CHANNEL_PRIO}, ${MAX_CHANNEL_PRIO}].`
        )
    }

    if (ports[0].port.priority !== 0) {
        throw new ConflictError(ErrorCode.conflict, `The first channel priority must be 0.`)
    }

    // Validate mergeability of ports with the same channel priority
    const portsGroupedByChannel = group(ports, (p) => p.port.priority!.toString())
    for (const [channelPriority, portsSharingChannel] of Object.entries(portsGroupedByChannel)) {
        const isMerging = portsSharingChannel.length > 1
        if (!isMerging) {
            continue
        }
        const isMergable = portsSharingChannel.every((p) => isMergeablePort(p.port))
        if (!isMergable) {
            throw new ConflictError(
                ErrorCode.conflict,
                `Protocols of ports with received channel priority ${channelPriority} cannot be merged (only RTP with no failoverPriority, RTP-over-SRT, RIST simple profile, or bonded SRT ports can be merged).`
            )
        }
        const isOnSameAppliance = portsSharingChannel.every(
            (p) => p.appliance.id === portsSharingChannel[0].appliance.id
        )
        if (!isOnSameAppliance && hasDerivedInputs) {
            throw new ConflictError(
                ErrorCode.conflict,
                `Inputs that merge channels across appliances cannot be used for transformations.`
            )
        }
        if (
            !isOnSameAppliance &&
            !areChannelsMergableAcrossAppliances({ logicalPorts: portsSharingChannel.map((p) => p.port) })
        ) {
            throw new ConflictError(
                ErrorCode.conflict,
                `Protocols of ports with received channel priority ${channelPriority} cannot be merged across appliances (only RTP with no failoverPriority, RTP-over-SRT, or RIST simple profile can be merged across appliances).`
            )
        }
    }

    // Validate that all SRT bonded ports on the same appliance share the same channel
    const portsGroupedByAppliance = group(ports, (p) => p.appliance.id)
    for (const [_applianceId, portsSharingAppliance] of Object.entries(portsGroupedByAppliance)) {
        let minPrio = -1
        for (const p of portsSharingAppliance) {
            if (p.port.priority! < minPrio) {
                throw new ConflictError(
                    ErrorCode.conflict,
                    `Channel priorities on the same appliance must be in increasing order. ${p.port.priority} < ${minPrio}. ${portsSharingAppliance.map((p) => p.port.priority).join(', ')}`
                )
            }
            minPrio = p.port.priority!
        }
        const bondedSrtPorts = portsSharingAppliance.filter((p) => isBondedSrtInputPort(p.port))
        if (bondedSrtPorts.length === 0) {
            continue
        }
        const hasSameChannel = bondedSrtPorts.every((p) => p.port.priority === bondedSrtPorts[0].port.priority)
        if (!hasSameChannel) {
            throw new ConflictError(
                ErrorCode.conflict,
                `SRT bonded input ports on the same appliance must share the same channel priority.`
            )
        }
    }

    // RTP ports with failover priority specified cannot share the same channel
    const unmergableRtpPorts = ports.filter((p) => p.port.mode === IpPortMode.rtp && !isRtpPortMergable(p.port))
    const unmergableRtpPortsByChannel = group(unmergableRtpPorts, (rtpPort) => rtpPort.port.priority!.toString())
    for (const [channelPrio, portsSharingChannel] of Object.entries(unmergableRtpPortsByChannel)) {
        if (portsSharingChannel.length > 1) {
            throw new ConflictError(
                ErrorCode.conflict,
                `RTP input port with channel priority ${channelPrio} cannot have failoverPriority defined. Failover priority must be undefined when merging channels.`
            )
        }
    }
}

function isRtpPortMergable(inputPort: { mode: string; failoverPriority?: number }): boolean {
    // Rtp ports with legacy property 'failoverPriority' require separate channels and are hence not mergable.
    return (
        concreteStreamingMode(inputPort.mode as PortMode) === IpPortMode.rtp &&
        typeof inputPort.failoverPriority !== 'number'
    )
}

function isRistSimpleProfilePort(port: { mode: string; profile?: string }): port is RistSimpleProfileInputPort {
    return port.mode === IpPortMode.rist && port.profile === RistProfile.simple
}

// Helper function to check if a port can share a channel (is mergeable)
export function isMergeablePort(port: {
    mode: string
    failoverPriority?: number | undefined
    rtp?: boolean
    profile?: string
}) {
    return isRtpPortMergable(port) || isRtpOverSrt(port) || isBondedSrtInputPort(port) || isRistSimpleProfilePort(port)
}

export function areChannelsMergableAcrossAppliances({
    logicalPorts,
}: {
    logicalPorts: {
        mode: string
        rtp?: boolean
        failoverPriority?: number | undefined
        profile?: string
    }[]
}) {
    // Note: Bonded SRT inputs are added as udp inputs to ristserver and hence cannot be merged across appliances
    return (
        logicalPorts.every(isRtpOverSrt) ||
        logicalPorts.every(isRtpPortMergable) ||
        logicalPorts.every(isRistSimpleProfilePort)
    )
}
